import { useState } from "react";
import { AlertCircle, CloudUpload, Loader2, X } from "lucide-react";

import { useApp } from "@/context/AppContext";

/**
 * Two-banner stack shown above <main> for sync-related affordances:
 *
 * 1. **Migration prompt** — when a signed-in user has localStorage data
 *    but the server returned empty, offer a one-tap upload.
 * 2. **Sync error** — when any background sync.* call fails, surface the
 *    error so the user knows the in-memory state is ahead of the server.
 *
 * Renders nothing when both banners would be empty.
 */
export function InventoryMigrationBanner() {
  const {
    lots,
    cards,
    storageBins,
    migrationAvailable,
    syncError,
    clearSyncError,
    uploadLocalInventory,
  } = useApp();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const handleUpload = async () => {
    setBusy(true);
    try {
      await uploadLocalInventory();
    } catch {
      // syncError will pick up the message from AppContext.
    } finally {
      setBusy(false);
    }
  };

  const showMigration = migrationAvailable && !dismissed;
  if (!showMigration && !syncError) return null;

  return (
    <div className="space-y-2 border-b border-border bg-card px-4 py-2 md:px-6">
      {showMigration && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-xs text-foreground">
          <CloudUpload className="h-4 w-4 text-primary" />
          <span className="flex-1 min-w-0">
            You have <strong>{lots.length}</strong> lot
            {lots.length === 1 ? "" : "s"}, <strong>{cards.length}</strong> card
            {cards.length === 1 ? "" : "s"}, and{" "}
            <strong>{storageBins.length}</strong> bin
            {storageBins.length === 1 ? "" : "s"} stored locally. Upload them to
            your account so they sync across devices?
          </span>
          <button
            type="button"
            onClick={() => void handleUpload()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CloudUpload className="h-3.5 w-3.5" />
            )}
            {busy ? "Uploading…" : "Upload"}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            disabled={busy}
            className="rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Not now
          </button>
        </div>
      )}
      {syncError && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="h-4 w-4" />
          <span className="flex-1 min-w-0">{syncError}</span>
          <button
            type="button"
            onClick={clearSyncError}
            aria-label="Dismiss"
            className="rounded-md p-1 hover:bg-destructive/15"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
